/**
 * Configuration pour la nouvelle table de gestion avancée des rôles
 * Structure optimisée avec historique des modifications et métadonnées
 */

// Ressources disponibles dans le système
export const RESOURCES = {
  APPOINTMENTS: 'appointments',
  BENEFICIARIES: 'beneficiaries',
  CONTRACTS: 'contracts',
  EMPLOYEES: 'employees',
  ROLES: 'roles',
  SETTINGS: 'settings',
  PAYMENTS: 'payments',
  TASK_CATEGORIES: 'taskCategories',
  TASKS: 'tasks',
  REPORTS: 'reports',
  FINANCIAL: 'financial',
  ACCOUNTING: 'accounting',
  NOTIFICATIONS: 'notifications',
  DASHBOARD: 'dashboard',
  MAP: 'map',
  DISCUSSIONS: 'discussions',
  RESOURCES: 'resources',
  DEVIS: 'devis',
  DOCUMENTS: 'documents',
  LEAVES: 'leaves',
  MESSAGING: 'messaging'
};

// Actions disponibles pour chaque ressource
export const ACTIONS = {
  READ: 'read',
  WRITE: 'write', 
  CREATE: 'create',
  DELETE: 'delete',
  MANAGE: 'manage'
};

// Structure de la nouvelle table advanced_roles
export const ADVANCED_ROLES_CONFIG = {
  COLLECTION_NAME: 'advanced_roles',
  DEFAULT_ROLES: {
    SUPER_ADMIN: {
      id: 'super_admin',
      name: 'Super Administrateur',
      description: 'Accès complet à toutes les fonctionnalités du système avec droits étendus pour la direction',
      level: 100,
      isSystem: true,
      permissions: Object.values(RESOURCES).reduce((acc, resource) => {
        acc[resource] = {
          read: true,
          write: true,
          create: true,
          delete: true,
          manage: true
        };
        return acc;
      }, {})
    },
    ADMIN: {
      id: 'admin',
      name: 'Administrateur',
      description: 'Accès complet à la gestion du système pour les responsables informatiques',
      level: 90,
      isSystem: true,
      permissions: Object.values(RESOURCES).reduce((acc, resource) => {
        acc[resource] = {
          read: true,
          write: true,
          create: true,
          delete: true,
          manage: resource !== 'settings' // Les admins ne peuvent pas gérer tous les paramètres système mais peuvent gérer payments
        };
        return acc;
      }, {})
    },
    DIRECTION: {
      id: 'direction',
      name: 'Direction',
      description: 'Accès stratégique à la gestion et aux rapports pour la direction générale',
      level: 85,
      isSystem: false,
      permissions: {
        [RESOURCES.APPOINTMENTS]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.EMPLOYEES]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.CONTRACTS]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.REPORTS]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.FINANCIAL]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.ACCOUNTING]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.DASHBOARD]: { read: true, write: false, create: false, delete: false, manage: false }
      }
    },
    COORDINATEUR: {
      id: 'coordinateur',
      name: 'Coordinateur',
      description: 'Gestion des équipes, plannings et bénéficiaires pour la coordination des services',
      level: 80,
      isSystem: false,
      permissions: {
        [RESOURCES.APPOINTMENTS]: { read: true, write: true, create: true, delete: true, manage: true },
        [RESOURCES.BENEFICIARIES]: { read: true, write: true, create: true, delete: false, manage: true },
        [RESOURCES.EMPLOYEES]: { read: true, write: true, create: false, delete: false, manage: false },
        [RESOURCES.CONTRACTS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.REPORTS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.TASKS]: { read: true, write: true, create: true, delete: true, manage: true },
        [RESOURCES.DASHBOARD]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.DISCUSSIONS]: { read: true, write: true, create: true, delete: true, manage: true },
        [RESOURCES.RESOURCES]: { read: true, write: true, create: true, delete: false, manage: true },
        [RESOURCES.DEVIS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.DOCUMENTS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.LEAVES]: { read: true, write: true, create: true, delete: true, manage: true },
        [RESOURCES.MESSAGING]: { read: true, write: true, create: true, delete: false, manage: true }
      }
    },
    ASSISTANT_DE_VIE: {
      id: 'assistant_vie',
      name: 'Assistant de Vie',
      description: 'Accompagnement aux activités quotidiennes et maintien à domicile des personnes',
      level: 70,
      isSystem: false,
      permissions: {
        [RESOURCES.APPOINTMENTS]: { read: true, write: true, create: false, delete: false, manage: false },
        [RESOURCES.BENEFICIARIES]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.TASKS]: { read: true, write: true, create: false, delete: false, manage: false },
        [RESOURCES.NOTIFICATIONS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.DASHBOARD]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.DISCUSSIONS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.LEAVES]: { read: true, write: false, create: true, delete: true, manage: false },
        [RESOURCES.MESSAGING]: { read: true, write: true, create: true, delete: false, manage: false }
      }
    },
    AUXILIAIRE_FAMILIAL: {
      id: 'auxiliaire_familial',
      name: 'Auxiliaire Familial',
      description: 'Soutien aux familles pour la garde d\'enfants et l\'aide aux parents',
      level: 68,
      isSystem: false,
      permissions: {
        [RESOURCES.APPOINTMENTS]: { read: true, write: true, create: false, delete: false, manage: false },
        [RESOURCES.BENEFICIARIES]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.TASKS]: { read: true, write: true, create: false, delete: false, manage: false },
        [RESOURCES.NOTIFICATIONS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.DISCUSSIONS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.LEAVES]: { read: true, write: false, create: true, delete: true, manage: false },
        [RESOURCES.MESSAGING]: { read: true, write: true, create: true, delete: false, manage: false }
      }
    },
    AIDE_A_DOMICILE: {
      id: 'aide_domicile',
      name: 'Aide à Domicile',
      description: 'Aide aux tâches ménagères et accompagnement des personnes à domicile',
      level: 65,
      isSystem: false,
      permissions: {
        [RESOURCES.APPOINTMENTS]: { read: true, write: true, create: false, delete: false, manage: false },
        [RESOURCES.BENEFICIARIES]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.TASKS]: { read: true, write: true, create: false, delete: false, manage: false },
        [RESOURCES.DISCUSSIONS]: { read: true, write: false, create: false, delete: false, manage: false }
      }
    },
    ACCOMPAGNATEUR: {
      id: 'accompagnateur',
      name: 'Accompagnateur',
      description: 'Accompagnement personnalisé pour les déplacements et activités sociales',
      level: 63,
      isSystem: false,
      permissions: {
        [RESOURCES.APPOINTMENTS]: { read: true, write: true, create: false, delete: false, manage: false },
        [RESOURCES.BENEFICIARIES]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.TASKS]: { read: true, write: true, create: false, delete: false, manage: false },
        [RESOURCES.DISCUSSIONS]: { read: true, write: false, create: false, delete: false, manage: false }
      }
    },
    GARDE_ENFANT: {
      id: 'garde_enfant',
      name: 'Garde d\'Enfant',
      description: 'Garde d\'enfants à domicile, sorties d\'école et activités ludiques',
      level: 62,
      isSystem: false,
      permissions: {
        [RESOURCES.APPOINTMENTS]: { read: true, write: true, create: false, delete: false, manage: false },
        [RESOURCES.BENEFICIARIES]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.TASKS]: { read: true, write: true, create: false, delete: false, manage: false },
        [RESOURCES.DISCUSSIONS]: { read: true, write: false, create: false, delete: false, manage: false }
      }
    },
    GARDE_NOCTURNE: {
      id: 'garde_nocturne',
      name: 'Garde Nocturne',
      description: 'Présence de nuit pour surveillance et assistance aux personnes dépendantes',
      level: 66,
      isSystem: false,
      permissions: {
        [RESOURCES.APPOINTMENTS]: { read: true, write: true, create: false, delete: false, manage: false },
        [RESOURCES.BENEFICIARIES]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.TASKS]: { read: true, write: true, create: false, delete: false, manage: false },
        [RESOURCES.NOTIFICATIONS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.DISCUSSIONS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.MESSAGING]: { read: true, write: true, create: true, delete: false, manage: false }
      }
    },
    GARDE_JOUR: {
      id: 'garde_jour',
      name: 'Garde de Jour',
      description: 'Accueil de jour et activités diurnes pour personnes âgées ou dépendantes',
      level: 64,
      isSystem: false,
      permissions: {
        [RESOURCES.APPOINTMENTS]: { read: true, write: true, create: false, delete: false, manage: false },
        [RESOURCES.BENEFICIARIES]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.TASKS]: { read: true, write: true, create: false, delete: false, manage: false },
        [RESOURCES.DISCUSSIONS]: { read: true, write: false, create: false, delete: false, manage: false }
      }
    },
    ASSISTANT_ADMIN: {
      id: 'assistant_admin',
      name: 'Assistant Administratif',
      description: 'Gestion administrative des bénéficiaires, contrats et documents',
      level: 75,
      isSystem: false,
      permissions: {
        [RESOURCES.CONTRACTS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.REPORTS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.ACCOUNTING]: { read: true, write: true, create: true, delete: false, manage: true },
        [RESOURCES.APPOINTMENTS]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.DISCUSSIONS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.RESOURCES]: { read: true, write: true, create: true, delete: false, manage: true },
        [RESOURCES.DEVIS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.DOCUMENTS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.MESSAGING]: { read: true, write: true, create: true, delete: false, manage: false }
      }
    },
    INFIRMIER: {
      id: 'infirmier',
      name: 'Infirmier',
      description: 'Soins infirmiers à domicile et suivi médical des bénéficiaires',
      level: 78,
      isSystem: false,
      permissions: {
        [RESOURCES.APPOINTMENTS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.REPORTS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.TASKS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.DISCUSSIONS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.RESOURCES]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.MESSAGING]: { read: true, write: true, create: true, delete: false, manage: false }
      }
    },
    AIDE_SOIGNANT: {
      id: 'aide_soignant',
      name: 'Aide-Soignant',
      description: 'Aide aux soins d\'hygiène et de confort des personnes dépendantes',
      level: 72,
      isSystem: false,
      permissions: {
        [RESOURCES.APPOINTMENTS]: { read: true, write: true, create: false, delete: false, manage: false },
        [RESOURCES.TASKS]: { read: true, write: true, create: false, delete: false, manage: false },
        [RESOURCES.DISCUSSIONS]: { read: true, write: false, create: false, delete: false, manage: false }
      }
    },
    BENEVOLE: {
      id: 'benevole',
      name: 'Bénévole',
      description: 'Accompagnement bénévole et soutien aux activités sociales',
      level: 50,
      isSystem: false,
      permissions: {
        [RESOURCES.APPOINTMENTS]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.TASKS]: { read: true, write: true, create: false, delete: false, manage: false }
      }
    },
    BENEFICIAIRE: {
      id: 'beneficiaire',
      name: 'Bénéficiaire',
      description: 'Accès client au portail personnel pour consultation du planning',
      level: 30,
      isSystem: true,
      permissions: {
        [RESOURCES.APPOINTMENTS]: { read: true, write: false, create: false, delete: false, manage: false },
        [RESOURCES.NOTIFICATIONS]: { read: true, write: true, create: true, delete: false, manage: false },
        [RESOURCES.DASHBOARD]: { read: true, write: false, create: false, delete: false, manage: false }
      }
    },
    VISITEUR: {
      id: 'visiteur',
      name: 'Visiteur',
      description: 'Accès public limité aux informations générales',
      level: 10,
      isSystem: true,
      permissions: {
        [RESOURCES.DASHBOARD]: { read: true, write: false, create: false, delete: false, manage: false }
      }
    }
  }
};

// Structure du document dans la collection advanced_roles
export const ADVANCED_ROLE_SCHEMA = {
  id: 'string', // ID unique du rôle
  name: 'string', // Nom affiché du rôle
  description: 'string', // Description du rôle
  level: 'number', // Niveau hiérarchique (10-100)
  isSystem: 'boolean', // Si c'est un rôle système qui ne peut pas être supprimé
  isActive: 'boolean', // Si le rôle est actif
  permissions: 'object', // Structure: { resource: { read: boolean, write: boolean, ... } }
  createdAt: 'timestamp',
  updatedAt: 'timestamp',
  createdBy: 'string', // ID de l'utilisateur qui a créé le rôle
  updatedBy: 'string', // ID de l'utilisateur qui a modifié le rôle
  version: 'number' // Version du schéma de permissions
};

// Migration depuis l'ancien système
export const MIGRATION_STRATEGY = {
  OLD_TO_NEW: {
    'admin': 'admin',
    'user': 'assistant_vie', // Les utilisateurs standards deviennent assistants de vie
    'commercial': 'assistant_admin', // Les commerciaux deviennent assistants administratifs
    'technician': 'infirmier', // Les techniciens deviennent infirmiers
    'installer': 'aide_soignant' // Les installateurs deviennent aides-soignants
  }
};

/**
 * Convertit les anciennes permissions vers le nouveau format
 * @param {Object} oldPermissions - Anciennes permissions au format { resource: { read, write, create } }
 * @returns {Object} Nouvelles permissions au format étendu
 */
export const convertToAdvancedPermissions = (oldPermissions) => {
  const newPermissions = {};
  
  for (const [resource, perms] of Object.entries(oldPermissions)) {
    newPermissions[resource] = {
      read: perms.read || false,
      write: perms.write || false,
      create: perms.create || false,
      delete: perms.write || false, // Dans l'ancien système, write incluait delete
      manage: perms.write && perms.create // Manage nécessite write et create
    };
  }
  
  return newPermissions;
};

/**
 * Convertit les nouvelles permissions vers l'ancien format pour la rétrocompatibilité
 * @param {Object} newPermissions - Nouvelles permissions au format étendu
 * @returns {Object} Anciennes permissions au format { resource: { read, write, create } }
 */
export const convertToLegacyPermissions = (newPermissions) => {
  const oldPermissions = {};
  
  for (const [resource, perms] of Object.entries(newPermissions)) {
    oldPermissions[resource] = {
      read: perms.read || false,
      write: perms.write || false,
      create: perms.create || false
    };
  }
  
  return oldPermissions;
};
